import {
  ButtonType,
  Div,
  PricingTile,
  RadioOptionCardProps,
  Separator,
  Spacer,
  UpsaleSheet,
} from '@sendoutcards/quantum-design-ui'
import React, { useState } from 'react'
import { Transition } from 'src/chrome'
import connect from 'src/redux/connect'
import { openApp } from 'src/helpers/appHelpers'
import FeaturedCardsGrid from '../components/FeaturedCardsGrid'
import { Disclaimer } from 'src/marketing/plans/components'
import { discountDisclaimer } from 'src/marketing/plans/components/pricing_table/PricingTable'
import { openLaunchPage, openPlans } from 'src/redux/actions/routeAliases'
import { getCardCatalog } from 'src/redux/selectors/catalog'
import { CardCatalog } from 'src/catalog/types'
import Future from 'src/utils/Future'

type PromoProps = {
  promo?: string
  planOptions?: RadioOptionCardProps[]
  openPlans: () => void
  openLaunchPage: () => void
  cardCatalog: Future<CardCatalog>
}

type ExpandedOptionChild = {
  expandedChild?: React.ReactNode
}

type OptionType = ExpandedOptionChild & RadioOptionCardProps

// TODO: add remaining subscription options for future promos
const options: OptionType[] = [
  {
    title: '$97.00/Month',
    subtitle: 'For unlimited single card send',
    isFeatured: true,
    optionId: '3', // plan ID from Plan table
    isSelected: false,
    expandedChild: (
      <>
        <PricingTile
          features={[
            'Unlimited Heartfelt Prompting Cards** (Postage included!)',
            {
              feature:
                'Automated Sends*** tiered pricing: (postage not included)',
              subFeatures: [
                '$1.75 per card',
                '$1.60 per card for 25+',
                '$1.45 per card for 50+',
                '$1.30 per card for 100+',
              ],
            },
            'Premier discounted shipping on top-selling gifts',
            '30 percent discount on gifts*',
            'Premium storage',
          ]}
          title="Unlimited"
          billingInterval="Monthly"
          price="$97"
          priceSubtext="/month"
          hasShadow={false}
        />
        <Div outset={{ horizontal: 'x4' }}>
          <Separator orientation="horizontal" />
          <Spacer space="x2" />
          <Disclaimer messages={discountDisclaimer} />
        </Div>
      </>
    ),
  },
  {
    title: '$37.00/Month',
    subtitle: 'Send one free card per day',
    isFeatured: false,
    optionId: '6',
    isSelected: false,
    expandedChild: (
      <>
        <PricingTile
          features={[
            '30 Heartfelt Prompting Cards every month',
            {
              feature:
                'Automated Sends*** tiered pricing: (postage not included)',
              subFeatures: [
                '$2.00 per card (beyond 30 Heartfelt Prompting Cards)',
                '$1.85 per card for 25+',
                '$1.70 per card for 50+',
                '$1.55 per card for 100+',
              ],
            },
            '20 percent discount on gifts*',
            'Enhanced storage',
          ]}
          title="Card a Day"
          billingInterval="Monthly"
          price="$37"
          priceSubtext="/month"
          hasShadow={false}
        />
        <Div outset={{ horizontal: 'x4' }}>
          <Separator orientation="horizontal" />
          <Spacer space="x2" />
          <Disclaimer messages={discountDisclaimer} />
        </Div>
      </>
    ),
  },
]

const getPlan = (planId: string) => {
  return options.find(option => option.optionId === planId)
}

const Promo: React.FC<PromoProps> = props => {
  const { promo, openPlans, openLaunchPage, cardCatalog } = props
  const [numberOfOptions] = useState(1)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [promoId] = useState(promo)
  const promoOptionId =
    options.find(option => option.optionId === promoId)?.optionId || '3'
  const filteredOptions = options.filter(
    option => option.optionId === promoOptionId,
  )
  const submitAction = {
    title: 'Subscribe Now',
    type: 'secondary' as ButtonType,
    onClick: (optionId: string) => {
      setIsRedirecting(true)
      openApp('', {
        join: '',
        promo: getPlan(optionId)?.optionId ?? '3',
      })
    },
  }

  const handleClose = () => {
    setIsRedirecting(false)
    openLaunchPage()
  }

  const handleViewMore = () => {
    setIsRedirecting(true)
    openPlans()
  }

  const viewMoreLink = {
    onClick: () => handleViewMore(),
    title: 'View More Options >',
  }

  if (isRedirecting || !cardCatalog.value) {
    return (
      <Transition
        message="Loading..."
        messageStyle={{ color: 'black', fontWeight: 500 }}
      />
    )
  }

  return (
    <UpsaleSheet
      setIsOpen={() => handleClose()}
      isOpen={true}
      title="Want to send more?"
      description="To send more cards and to access some great benefits, upgrade to one of our paid accounts. You can send as many cards as you want (yes, thats right) on our $97 Unlimited subscription! Or a card a day on our $37 subscription! Click to sign up or view our other packages!"
      options={filteredOptions}
      submitAction={submitAction}
      extraLink={viewMoreLink}
      selectedOptionTitle="Great choice"
      selectedOptionDescription="We are so happy with your choice"
      backgroundElement={
        <FeaturedCardsGrid
          featuredCards={
            cardCatalog.value ? cardCatalog.value.featuredCards : []
          }
        />
      }
      defaultNumOptionsShown={numberOfOptions}
      selectedOption={''}
    />
  )
}

export default connect(
  state => {
    return { promo: state.promo.promo, cardCatalog: getCardCatalog(state) }
  },
  { openPlans, openLaunchPage },
)(Promo)
